/**
 * W8 — Versions & supplements. The append-only history of a plan: a version
 * timeline (D1 — status draft/review/approved/locked, actor, ts, summary), a
 * version diff (what changed between the selected version and its prior), the
 * AFE supplements table + request form (D24 — authorized_total = original +
 * Σ approved), and the multi-step approval trail. Wired to pApi `versions`
 * (getEventPlans), `supplements`, and the PATCH status endpoints (MSW-seeded).
 *
 * Backend rules reflected as UI states (never a crash): a plan is publishable
 * only once approved/locked; a locked/published plan rejects new supplements
 * (the request form is disabled with a message). Built to
 * w8-versions-supplements.html on the P3 kit.
 */
import { useMemo, useState } from "react";
import { useAppSelector } from "@app/hooks";
import { useLocale } from "@hooks/useLocale";
import { useGetWellsQuery } from "@api/mApi";
import {
  useGetWellEventsQuery,
  useGetEventPlansQuery,
  useGetPlanSupplementsQuery,
  useGetPlanDatasetsQuery,
  useCreateSupplementMutation,
  useSetSupplementStatusMutation,
} from "@api/pApi";
import type { EventOut, PlanOut, SupplementOut, PlanDatasets } from "@api/types";
import type { WellView } from "@api/mappers";
import { asArray } from "@lib/asArray";
import { fmtMoney, fmtNum } from "@lib/format";
import { enrichEventCode, EVENT_LETTER_OP_VAR } from "@lib/eventEnrichment";
import { STATUS_KEY } from "@features/planFinder/planCatalog";
import { QueryState } from "@components/ui/QueryState";
import {
  versionList,
  currentPlan,
  priorVersion,
  supplementRollup,
  projectedTotal,
  buildDiff,
  canPublish,
  acceptsSupplements,
  type SupplementRollup,
} from "./versionsModel";
import "./versions.css";

/** Status → the version-timeline item's CSS modifier (dot color). */
function timelineClass(plan: PlanOut, isCurrent: boolean): string {
  if (isCurrent) return "current";
  if (plan.status === "approved" || plan.status === "published") return "approved";
  if (plan.status === "locked") return "locked";
  return "";
}
/** Status → the pill CSS modifier (W8's 5-state palette). */
function pillClass(status: PlanOut["status"]): string {
  return status; // draft/review/approved/locked/published all have a .status-pill rule
}

export function VersionsScreen() {
  const { t } = useLocale();

  const selectedWellId = useAppSelector((s) => s.wellContext.selectedWellId);
  const activeEventId = useAppSelector((s) => s.wellContext.activeEventId);

  const wellsQ = useGetWellsQuery();
  const well = useMemo(() => {
    const wells = asArray<WellView>(wellsQ.data);
    return wells.find((w) => w.id === selectedWellId) ?? wells[0] ?? null;
  }, [wellsQ.data, selectedWellId]);

  const eventsQ = useGetWellEventsQuery(well?.uwi ?? "", { skip: !well });
  const events = useMemo(() => asArray<EventOut>(eventsQ.data), [eventsQ.data]);
  const activeEvent = useMemo<EventOut | null>(() => {
    if (events.length === 0) return null;
    return events.find((e) => e.id === activeEventId) ?? events[0] ?? null;
  }, [events, activeEventId]);

  return (
    <div>
      <div className="screen-head">
        <h1>{t("versions.title")}</h1>
        <span className="badge info">{t("versions.badge")}</span>
      </div>

      <QueryState
        isLoading={wellsQ.isLoading || (!!well && eventsQ.isLoading)}
        isError={wellsQ.isError || eventsQ.isError}
        isEmpty={!well || events.length === 0}
        emptyMessage={t("versions.empty")}
        onRetry={() => void eventsQ.refetch()}
      >
        {well && activeEvent && (
          <VersionsBody wellName={well.commonName} activeEvent={activeEvent} />
        )}
      </QueryState>
    </div>
  );
}

/** The body once a well + event are resolved (owns versions + supplements). */
function VersionsBody({ wellName, activeEvent }: { wellName: string; activeEvent: EventOut }) {
  const { t } = useLocale();

  const plansQ = useGetEventPlansQuery(activeEvent.id);
  const plans = useMemo(() => versionList(plansQ.data), [plansQ.data]);
  const current = useMemo(() => currentPlan(plansQ.data), [plansQ.data]);

  const supplementsQ = useGetPlanSupplementsQuery(current?.id ?? "", { skip: !current });
  const rollup = useMemo(
    () => supplementRollup(current, supplementsQ.data),
    [current, supplementsQ.data],
  );

  const en = enrichEventCode(activeEvent.event_code);
  const conv = en.isConventional ?? activeEvent.is_conventional;
  const opVar = en.letter ? EVENT_LETTER_OP_VAR[en.letter] : "--surface-3";

  // Selected version for the diff panel (defaults to the current version).
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = useMemo(
    () => plans.find((p) => p.id === selectedId) ?? current,
    [plans, selectedId, current],
  );

  return (
    <>
      {/* context chips: well · event · afe · status · version */}
      <div className="chip-ctx" style={{ marginBottom: 16, flexWrap: "wrap" }}>
        <span className="k">{t("versions.ctx.well")}</span>
        <span className="mono">{wellName}</span>
        <span className="dim">·</span>
        <span className="k">{t("versions.ctx.event")}</span>
        <span className="letter-badge" style={{ background: `var(${opVar})` }}>
          <span className="sw" />
          {en.letter ?? "?"} · {activeEvent.event_code} — {en.name}
        </span>
        {(current?.afe_no ?? activeEvent.afe_no) && (
          <>
            <span className="dim">·</span>
            <span className="k">{t("versions.ctx.afe")}</span>
            <span className="mono">{current?.afe_no ?? activeEvent.afe_no}</span>
          </>
        )}
        {current && (
          <>
            <span className={`status-pill ${pillClass(current.status)}`}>
              ● {t(STATUS_KEY[current.status], { n: current.version })}
            </span>
            <span className="verchip">v{current.version}</span>
          </>
        )}
        {conv === false && (
          <span className="badge crit" title={t("conv.unconvention")}>
            {t("conv.unconvention")}
          </span>
        )}
      </div>

      <div className="section-note">
        <span className="qm">i</span>
        <span>{t("versions.note")}</span>
      </div>

      <QueryState
        isLoading={plansQ.isLoading}
        isError={plansQ.isError}
        isEmpty={plans.length === 0}
        emptyMessage={t("versions.noVersions")}
        onRetry={() => void plansQ.refetch()}
      >
        {/* AFE authorization rollup (KPI chips + inline equation) */}
        <RollupStrip rollup={rollup} currency={current?.currency} />

        {/* version timeline (left) + diff panel (right) */}
        <div className="grid split" style={{ marginBottom: 20 }}>
          <section className="card" style={{ padding: 16 }}>
            <div className="rowflex" style={{ marginBottom: 10 }}>
              <h2 style={{ margin: 0 }}>{t("versions.history.title")}</h2>
              <span
                className={`status-pill ${canPublish(current?.status) ? "approved" : "draft"}`}
                title={t("versions.history.publishHint")}
              >
                ● {t(canPublish(current?.status) ? "versions.publish.ready" : "versions.publish.notReady")}
              </span>
            </div>
            <ul className="vtimeline">
              {plans.map((p) => (
                <VersionItem
                  key={p.id}
                  plan={p}
                  isCurrent={p.id === current?.id}
                  isSelected={p.id === selected?.id}
                  onSelect={() => setSelectedId(p.id)}
                />
              ))}
            </ul>
          </section>

          <DiffPanel
            selected={selected}
            prior={selected ? priorVersion(plans, selected.version) : null}
          />
        </div>

        {/* supplements table + request form */}
        <SupplementsCard plan={current} supplements={supplementsQ.data} rollup={rollup} />

        {/* approval trail for the first in-flight supplement */}
        <ApprovalTrail plan={current} supplements={supplementsQ.data} />
      </QueryState>
    </>
  );
}

/** KPI chips + the inline original + Σapproved = authorized equation. */
function RollupStrip({ rollup, currency }: { rollup: SupplementRollup; currency?: string }) {
  const { t, locale } = useLocale();
  const money = (n: number) => fmtMoney(n, locale, { compact: true, currency });
  const full = (n: number) => fmtMoney(n, locale, { currency });
  return (
    <>
      <div className="kpis">
        <div className="kpi" data-own="m">
          <div className="v tab-nums">{money(rollup.original)}</div>
          <div className="l">{t("versions.kpi.original")}</div>
        </div>
        <div className="kpi" data-own="p">
          <div className="v tab-nums good">
            +{money(rollup.approvedTotal)}
          </div>
          <div className="l">{t("versions.kpi.approved", { n: rollup.approvedCount })}</div>
        </div>
        <div className="kpi" data-own="p">
          <div className="v tab-nums warn">+{money(rollup.pendingTotal)}</div>
          <div className="l">{t("versions.kpi.pending", { n: rollup.pendingCount })}</div>
        </div>
        <div className="kpi" data-own="z" style={{ borderColor: "var(--brand)" }}>
          <div className="v tab-nums" style={{ color: "var(--head)" }}>
            {money(rollup.authorizedTotal)}
          </div>
          <div className="l">{t("versions.kpi.authorized")}</div>
        </div>
      </div>

      <div className="rollup">
        <span className="term">{full(rollup.original)}</span>
        <span className="op">({t("versions.rollup.original")})</span>
        <span className="eq">+</span>
        <span className="term good">{full(rollup.approvedTotal)}</span>
        <span className="op">({t("versions.rollup.approved", { n: rollup.approvedCount })})</span>
        <span className="eq">=</span>
        <span className="total tab-nums">{full(rollup.authorizedTotal)}</span>
        <span className="op">{t("versions.rollup.authorized")}</span>
      </div>
    </>
  );
}

/** One version-timeline row (a selectable card). */
function VersionItem({
  plan,
  isCurrent,
  isSelected,
  onSelect,
}: {
  plan: PlanOut;
  isCurrent: boolean;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const { t } = useLocale();
  const cls = timelineClass(plan, isCurrent);
  return (
    <li className={`vitem ${cls}${isSelected ? " selected" : ""}`}>
      <span className="vdot">v{plan.version}</span>
      <button
        type="button"
        className="vbody"
        aria-pressed={isSelected}
        onClick={onSelect}
      >
        <div className="vhead">
          <b>v{plan.version}</b>
          <span className={`status-pill ${pillClass(plan.status)}`}>
            ● {t(STATUS_KEY[plan.status], { n: plan.version })}
          </span>
          {plan.actor && <span className="who">{plan.actor}</span>}
        </div>
        {plan.summary && <div className="vsum">{plan.summary}</div>}
        <div className="vmeta">
          <span className="chgtag">{plan.updated_at.slice(0, 10)}</span>
          {plan.published_at && <span>{t("versions.item.published")}</span>}
        </div>
      </button>
    </li>
  );
}

/** The version diff panel — compares the selected version with its prior. */
function DiffPanel({ selected, prior }: { selected: PlanOut | null; prior: PlanOut | null }) {
  const { t, locale } = useLocale();
  const afterQ = useGetPlanDatasetsQuery(selected?.id ?? "", { skip: !selected });
  const beforeQ = useGetPlanDatasetsQuery(prior?.id ?? "", { skip: !prior });

  const groups = useMemo(
    () =>
      buildDiff(afterQ.data as PlanDatasets | undefined, beforeQ.data as PlanDatasets | undefined, {
        num: (n) => fmtNum(n, locale),
        money: (n) => fmtMoney(n, locale, { currency: selected?.currency }),
      }),
    [afterQ.data, beforeQ.data, locale, selected?.currency],
  );

  return (
    <section className="card" style={{ padding: 16 }}>
      <div className="rowflex" style={{ marginBottom: 4 }}>
        <h2 style={{ margin: 0 }}>{t("versions.diff.title")}</h2>
        <span className="verchip">
          {selected
            ? prior
              ? t("versions.diff.chip", { a: selected.version, b: prior.version })
              : t("versions.diff.initial", { a: selected.version })
            : "—"}
        </span>
      </div>
      <p className="dim" style={{ fontSize: 12.5, margin: "0 0 12px" }}>
        {t("versions.diff.sub")}
      </p>

      <QueryState
        isLoading={afterQ.isLoading || (!!prior && beforeQ.isLoading)}
        isError={afterQ.isError}
        onRetry={() => void afterQ.refetch()}
      >
        {groups.map((g) => (
          <div key={g.titleKey}>
            <h3 style={{ margin: "14px 0 6px" }}>{t(g.titleKey as never)}</h3>
            <div className="diffgrid">
              <div className="dh">{t("versions.diff.field")}</div>
              <div className="dh">{t("versions.diff.before")}</div>
              <div className="dh" />
              <div className="dh">{t("versions.diff.after")}</div>
              {g.rows.map((r, i) => (
                <div key={i} className={`diffrow${r.changed ? " changed" : ""}`}>
                  <div className="lbl">{r.rawLabel ? r.label : t(r.label as never)}</div>
                  <div className="old">{r.before ?? "—"}</div>
                  <div className="arrow">{r.changed ? "→" : "="}</div>
                  <div className="new">{r.after ?? "—"}</div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="section-note" style={{ margin: "16px 0 0" }}>
          <span className="qm">i</span>
          <span>{t("versions.diff.note")}</span>
        </div>
      </QueryState>
    </section>
  );
}

/** The supplements table + inline request form (D24). */
function SupplementsCard({
  plan,
  supplements,
  rollup,
}: {
  plan: PlanOut | null;
  supplements: unknown;
  rollup: SupplementRollup;
}) {
  const { t, locale } = useLocale();
  const supps = useMemo(() => asArray<SupplementOut>(supplements), [supplements]);
  const accepts = acceptsSupplements(plan?.status);

  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [createSupplement, createState] = useCreateSupplementMutation();

  const amountNum = Number(amount.replace(/,/g, ""));
  const projected = projectedTotal(rollup, amountNum);
  const canSubmit = accepts && reason.trim().length > 0 && Number.isFinite(amountNum) && amountNum > 0;

  async function submit() {
    if (!plan || !canSubmit) return;
    await createSupplement({ planId: plan.id, amount: amountNum, reason: reason.trim() }).unwrap();
    setReason("");
    setAmount("");
    setOpen(false);
  }

  return (
    <section className="card" style={{ padding: 16, marginBottom: 20 }}>
      <div className="rowflex" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>{t("versions.supp.title")}</h2>
        <div className="actions">
          <button
            type="button"
            className="btn primary"
            disabled={!accepts}
            title={accepts ? undefined : t("versions.supp.lockedHint")}
            onClick={() => setOpen((v) => !v)}
          >
            <span className="ic ic-add" aria-hidden="true" /> {t("versions.supp.request")}
          </button>
        </div>
      </div>

      {!accepts && (
        <div className="section-note">
          <span className="qm">!</span>
          <span>{t("versions.supp.lockedMsg")}</span>
        </div>
      )}

      {supps.length === 0 ? (
        <div className="empty-state" style={{ color: "var(--dim)" }}>
          {t("versions.supp.empty")}
        </div>
      ) : (
        <div className="table-wrap">
          <table className="dt">
            <thead>
              <tr>
                <th>{t("versions.supp.col.seq")}</th>
                <th>{t("versions.supp.col.reason")}</th>
                <th className="num">{t("versions.supp.col.amount")}</th>
                <th>{t("versions.supp.col.by")}</th>
                <th>{t("versions.supp.col.status")}</th>
                <th>{t("versions.supp.col.date")}</th>
              </tr>
            </thead>
            <tbody>
              {supps.map((s) => (
                <tr key={s.id}>
                  <td className="mono">#{s.seq}</td>
                  <td>{s.reason}</td>
                  <td
                    className={`num ${s.status === "approved" ? "good" : s.status === "requested" ? "warn" : "dim"}`}
                  >
                    +{fmtNum(s.amount, locale)}
                  </td>
                  <td>{s.actor ?? t("common.na")}</td>
                  <td>
                    <span className={`status-pill ${s.status === "requested" ? "review" : s.status}`}>
                      ● {t(`versions.supp.state.${s.status}` as never)}
                    </span>
                  </td>
                  <td className="tab-nums">{s.created_at.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>{t("versions.supp.sumApproved")}</td>
                <td className="num good">+{fmtNum(rollup.approvedTotal, locale)}</td>
                <td colSpan={3} className="dim" style={{ fontWeight: 400 }}>
                  {fmtNum(rollup.original, locale)} + {fmtNum(rollup.approvedTotal, locale)} ={" "}
                  <strong style={{ color: "var(--head)" }}>
                    {fmtNum(rollup.authorizedTotal, locale)}
                  </strong>{" "}
                  {t("versions.supp.authorizedNote")}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {open && accepts && (
        <div className="suppform">
          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 0 12px" }}>
            <h3 style={{ margin: 0 }}>{t("versions.supp.formTitle")}</h3>
            {plan?.afe_no && (
              <span className="dim" style={{ fontSize: 12 }}>
                {t("versions.supp.formSub", { afe: plan.afe_no })}
              </span>
            )}
          </div>

          <div className="mform">
            <div className="mfield full">
              <label className="req" htmlFor="suppReason">
                {t("versions.supp.f.reason")}
              </label>
              <textarea
                id="suppReason"
                rows={2}
                value={reason}
                placeholder={t("versions.supp.f.reasonPh")}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            <div className="mfield">
              <label className="req" htmlFor="suppAmount">
                {t("versions.supp.f.amount")}
              </label>
              <div className="field">
                <span className="unit">US$</span>
                <input
                  id="suppAmount"
                  type="number"
                  inputMode="decimal"
                  className="tab-nums"
                  placeholder="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
            </div>
            <div className="mfield">
              <label>{t("versions.supp.f.projected")}</label>
              <input
                value={fmtMoney(projected, locale, { currency: plan?.currency })}
                disabled
                className="tab-nums"
              />
            </div>
          </div>

          <div className="rowflex" style={{ marginTop: 6 }}>
            <span className="dim" style={{ fontSize: 12 }}>
              {t("versions.supp.f.hint")}
            </span>
            <div className="actions" style={{ display: "flex", gap: 8 }}>
              <button type="button" className="btn" onClick={() => setOpen(false)}>
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="btn primary"
                disabled={!canSubmit || createState.isLoading}
                onClick={() => void submit()}
              >
                <span className="ic ic-check" aria-hidden="true" />
                {createState.isLoading ? t("versions.supp.f.sending") : t("versions.supp.f.send")}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/** The multi-step approval trail for the first in-flight supplement. */
function ApprovalTrail({ plan, supplements }: { plan: PlanOut | null; supplements: unknown }) {
  const { t } = useLocale();
  const supps = useMemo(() => asArray<SupplementOut>(supplements), [supplements]);
  const [setStatus, statusState] = useSetSupplementStatusMutation();

  // Show the trail for the first supplement still in flight (requested).
  const active = supps.find((s) => s.status === "requested") ?? supps[0] ?? null;
  const steps = asArray<NonNullable<SupplementOut["approvals"]>[number]>(active?.approvals);

  if (!active || steps.length === 0) {
    return (
      <section className="card" style={{ padding: 16 }}>
        <h2 style={{ margin: "0 0 4px" }}>{t("versions.trail.title")}</h2>
        <div className="empty-state" style={{ color: "var(--dim)" }}>
          {t("versions.trail.empty")}
        </div>
      </section>
    );
  }

  const stageClass = (status: string) =>
    status === "approved"
      ? "done"
      : status === "pending"
        ? "active"
        : status === "rejected"
          ? "rejected"
          : "locked";

  async function decide(next: "approved" | "rejected") {
    if (!plan || !active) return;
    await setStatus({ planId: plan.id, supplementId: active.id, status: next }).unwrap();
  }

  return (
    <section className="card" style={{ padding: 16 }}>
      <div className="rowflex" style={{ marginBottom: 4 }}>
        <h2 style={{ margin: 0 }}>{t("versions.trail.title")}</h2>
        <span className="verchip">{t("versions.trail.chip", { seq: active.seq })}</span>
      </div>
      <p className="dim" style={{ fontSize: 12.5, margin: "0 0 14px" }}>
        {t("versions.trail.sub")}
      </p>

      <div className="approval-steps">
        {steps.map((step, i) => (
          <div key={i} className={`astep ${stageClass(step.status)}`}>
            <div className="aico">
              {step.status === "approved" ? "✓" : step.status === "locked" ? "🔒" : i + 1}
            </div>
            <div className="abody">
              <div className="ah">
                <b>
                  {i + 1} · {t(`versions.trail.stage.${step.stage}` as never)}
                </b>
                <span className={`status-pill ${step.status === "pending" ? "review" : step.status}`}>
                  ● {t(`versions.trail.state.${step.status}` as never)}
                </span>
              </div>
              <div className="asub">
                {step.actor ?? t("common.na")}
                {step.decided_at ? ` · ${step.decided_at.slice(0, 10)}` : ""}
                {step.note ? ` — ${step.note}` : ""}
              </div>
              {step.status === "pending" && (
                <div className="aactions">
                  <button
                    type="button"
                    className="btn sm"
                    disabled={statusState.isLoading}
                    onClick={() => void decide("approved")}
                  >
                    <span className="ic ic-check" aria-hidden="true" /> {t("versions.trail.approve")}
                  </button>
                  <button
                    type="button"
                    className="btn sm"
                    disabled={statusState.isLoading}
                    onClick={() => void decide("rejected")}
                  >
                    {t("versions.trail.reject")}
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
